import type { FormEventHandler } from 'react'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { forgotPassword, getUserKeyFromPhone, login, updateUserDetails } from 'actions/AuthActions'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import PasswordInput from 'components/Common/Form/Input/PasswordInput'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import noop from 'lib/function/noop'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { selectLoggedIn } from 'selectors/accountSelectors'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import * as Analytics from 'analytics/analytics'
import HelpPane from 'components/Common/HelpPane'
import ContactContext from 'contexts/contactContext'
import Caption from 'LuxKit/Typography/Caption'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

interface LoginForm {
  password: string
}

interface Props {
  error?: any
  user: App.User
  isStoreMode: boolean
  onModeChange?: (mode: App.UiAccountModalMode) => void
  dismissable?: boolean
}

function AccountAccessJoinExistingAccount(props: Props) {
  const {
    error,
    user,
    isStoreMode,
    onModeChange = noop,
    dismissable,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const nativeInputRef = useRef<HTMLInputElement>(null)
  const reRef = useRef<ReCAPTCHA>(null)
  const loggedIn = useAppSelector(selectLoggedIn)
  const currentContact = useContext(ContactContext)
  const contactNumber = currentContact.defaultContact
  const isMobile = useScreenSizeOnly('mobile')

  const currentPathName = useAppSelector(state => state.router.location.pathname)
  const currentSearch = useAppSelector(state => state.router.location.search)
  const callbackPath = currentPathName + currentSearch

  useEffect(() => {
    if (loggedIn) {
      if (user.phoneNumber) {
        dispatch(updateUserDetails({
          phone: user.phoneNumber.phone,
          phonePrefix: user.phoneNumber.prefix,
        },
        ))
      }
      modalContext?.resolve({ loggedIn: true })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'sign_in_existing_account_found',
      action: 'impression',
      category: 'logging',
      type: 'nonInteraction',
    })

    if (!isMobile) {
      nativeInputRef.current?.focus()
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onForgotPassword = useCallback(() => {
    if (user.email) {
      // forgot password only works with an email, not phone number (for now?)
      dispatch(forgotPassword(user.email, callbackPath))
      onModeChange('forgotPassword')
    }
  }, [dispatch, onModeChange, user, callbackPath])

  const onLogin = useCallback<FormEventHandler<HTMLFormElement>>(async(event) => {
    event.preventDefault()
    const loginData = formToObject<LoginForm>(event.currentTarget)
    const recaptchaData = await reRef?.current?.executeAsync()
    if (recaptchaData) {
      dispatch(login(user, loginData.password, recaptchaData))
    }
  }, [dispatch, user])

  const onBack = useCallback(() => {
    // go back to join and swap back to the phone number user
    // (we're currently on the 'email' user they entered for registration)
    onModeChange('join', getUserKeyFromPhone(user.phoneNumber!))
  }, [onModeChange, user])

  const existingAccountTitle = intl.formatMessage({
    id: 'nyk2Rbiv8ElLY04cJwxV',
    defaultMessage: 'Existing account found',
    description: 'Modal title when an existing account is found during sign-up',
  })

  const serverErrorText = intl.formatMessage({
    id: 'eTjBaTf9KIE9Ok-nFcu5',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Generic server error message on existing account login',
  })

  // existingAccountText uses FormattedMessage inline (rich text with <b>)

  const passwordLabel = intl.formatMessage({
    id: 'CarEJFLwOBwuEZPLIwW1',
    defaultMessage: 'Password',
    description: 'Label for password input on existing account login',
  })

  const passwordPlaceholder = intl.formatMessage({
    id: 'SFVG5Gxeai9H4lStLNGX',
    defaultMessage: 'Password',
    description: 'Placeholder for password input on existing account login',
  })

  const passwordRequired = intl.formatMessage({
    id: 'tG1l4CMcqw9LVNU2SGtc',
    defaultMessage: 'Password is required',
    description: 'Validation error when password is empty on existing account login',
  })

  const loginWithPasswordLabel = intl.formatMessage({
    id: 'Yg0k09CjZ2e5Xoe7HayM',
    defaultMessage: 'Log in with password',
    description: 'Button text to log in with password on existing account form',
  })

  const forgotPasswordLabel = intl.formatMessage({
    id: 'o5HbLl6X3vL-LkyXoity',
    defaultMessage: 'Forgot your password?',
    description: 'Link text to reset password on existing account login',
  })

  const phoneUpdateCaption = intl.formatMessage({
    id: 'TxC9TnezVzGWz4yjoCYk',
    defaultMessage: 'By continuing with login, your phone number will be updated with the one you entered during the initial sign-up process.',
    description: 'Caption explaining phone number update when logging in with existing account',
  })

  return (<>
    <ModalHeader
      title={existingAccountTitle}
      onBackButtonClick={onBack}
      dismissible={dismissable}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24} as="form" onSubmit={onLogin} name="loginForm">
          {error?.status === 500 && <MessageBanner kind="critical">{serverErrorText}</MessageBanner>}
          {error?.message && error.status !== 500 && <MessageBanner kind="critical">{error.message}</MessageBanner>}
          <VerticalSpacer gap={16}>
            <BodyText variant="large">
              <FormattedMessage
                id="58PNsZdsEp4BBrYQytJq"
                defaultMessage="You already have an account registered under <b>{email}</b>. Please enter your password to log in."
                description="Instructional text when existing account is found during sign-up"
                values={{
                  email: user.email,
                  b: (chunks) => <b>{chunks}</b>,
                }}
              />
            </BodyText>
            <fieldset>
              <PasswordInput
                label={passwordLabel}
                id="password"
                required
                name="password"
                placeholder={passwordPlaceholder}
                requiredErrorMessage={passwordRequired}
                ref={nativeInputRef}
              />
              <Group direction="vertical" gap={16} horizontalAlign="center">
                <TextButton
                  kind="primary"
                  size="large"
                  type="submit"
                  fit="flex"
                >
                  {loginWithPasswordLabel}
                </TextButton>
                {!isStoreMode && <TextLink
                  onClick={onForgotPassword}
                  size="medium"
                >
                  {forgotPasswordLabel}
                </TextLink>}
              </Group>
              {error?.status === 500 && (
                <HelpPane contactNumber={contactNumber} />
              )}
            </fieldset>
            {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
            {user.phoneNumber && <Caption variant="medium" colour="neutral-three">
              {phoneUpdateCaption}
            </Caption>}
          </VerticalSpacer>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    isStoreMode: state.system.storeMode,
  }
}

export default connect(mapStateToProps)(AccountAccessJoinExistingAccount)
